import React, {
  useRef,
  forwardRef,
  useImperativeHandle,
  useEffect,
  useState,
} from "react";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  flexRender,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import SimpleTooltip from "./SimpleToolTip";
import ColumnMenu from "./ColumnMenu";

const VirtualizedTable = forwardRef(
  (
    {
      data,
      columns,
      height = 500,
      showCheckbox = true,
      hiddenColumns = [],
      initialSelectedRows = [],
      getRowStyle = () => ({}),
    },
    ref
  ) => {

    const [columnVisibility, setColumnVisibility] = useState({});
    const enhancedColumns = showCheckbox
      ? [
          {
            id: "select",
            enableResizing: false,
            header: ({ table }) => (
              <input
                type="checkbox"
                checked={table.getIsAllPageRowsSelected?.()}
                onChange={table.getToggleAllPageRowsSelectedHandler?.()}
              />
            ),
            cell: ({ row }) => (
              <input
                type="checkbox"
                checked={row.getIsSelected?.()}
                disabled={!row.getCanSelect?.()}
                onChange={row.getToggleSelectedHandler?.()}
              />
            ),
            size: 40,
          },
          ...columns,
        ]
      : columns;

    const visibleColumns = enhancedColumns.filter(
      (col) => !hiddenColumns.includes(col.accessorKey ?? col.id)
    );

    const table = useReactTable({
      data,
      columns: visibleColumns,
      state: {
        columnVisibility,
      },
      onColumnVisibilityChange: setColumnVisibility,
      getCoreRowModel: getCoreRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
      getSortedRowModel: getSortedRowModel(),
      enableRowSelection: showCheckbox,
      columnResizeMode: "onChange",
        getRowId: (row) => row.TripNo.toString(), // NOTE: AA: : LATER IT NEEDS TO BE DYNAMIC (25 September 2025)
    });

    useEffect(() => {
      if (initialSelectedRows && table) {
        table.setRowSelection(initialSelectedRows);
      }
    }, [initialSelectedRows, table]);

    useImperativeHandle(ref, () => ({
      getSelectedRows: () =>
        table.getSelectedRowModel().rows.map((r) => r.original),
    }));

    const parentRef = useRef(null);
    const rowVirtualizer = useVirtualizer({
      count: table.getRowModel().rows.length,
      getScrollElement: () => parentRef.current,
      estimateSize: () => 40,
      overscan: 10,
    });

    const gridTemplateColumns = table
      .getAllLeafColumns()
      .map((col) => `${col.getSize()}px`)
      .join(" ");
    const totalTableWidth = table.getTotalSize();

    return (
      <div>
        <div
          ref={parentRef}
          style={{
            height: `${height}`,
            overflow: "auto",
            border: "1px solid #ccc",
            borderRadius: "8px",
            position: "relative",
          }}
        >
          {/* Header */}
          <div
            style={{
              display: "grid",
              width: `${totalTableWidth}px`,
              gridTemplateColumns,
              borderBottom: "2px solid #ccc",
              background: "rgb(219 219 219)",
              position: "sticky",
              top: 0,
              zIndex: 1,
              gap: "4px",
            }}
          >
            {table.getHeaderGroups().map((headerGroup) =>
              headerGroup.headers.map((header) => (
                <div
                  key={header.id}
                  style={{
                    minWidth: header.column.getSize(),
                    padding: "4px 8px",
                    fontWeight: "bold",
                    position: "relative",
                    userSelect: "none",
                    cursor: header.column.getCanSort()
                      ? "pointer"
                      : "default",
                    borderRight: "1px solid #ddd",
                  }}
                  onClick={(e) => {
                    if (
                      e.target.tagName === "INPUT" ||
                      e.target.tagName === "BUTTON" ||
                      e.target.closest("input") ||
                      e.target.closest("button")
                    ) {
                      return;
                    }
                    header.column.getToggleSortingHandler()?.(e);
                  }}
                >
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext()
                  )}
                  {header.column.getCanSort() && (
                    <span>
                      {{
                        asc: " ▲",
                        desc: " ▼",
                      }[header.column.getIsSorted()] ?? ""}
                    </span>
                  )}

                    {/* 3-dot menu */}
                    {header.column.id !== "select" && ( 
                      <div style={{ position: "absolute", top: "4px", right: "4px" }}>
                        <ColumnMenu table={table} />
                      </div>
                    )}


                  {/* Filter input */}
                  {header.column.getCanFilter() ? (
                    <div style={{ position: "relative", marginTop: "4px" }}>
                      <input
                        type="text"
                        placeholder="Filter..."
                        value={header.column.getFilterValue() ?? ""}
                        onChange={(e) =>
                          header.column.setFilterValue(e.target.value)
                        }
                        style={{
                          width: "100%",
                          padding: "4px 22px 4px 6px",
                          fontSize: "12px",
                          boxSizing: "border-box",
                        }}
                      />
                      {header.column.getFilterValue() ? (
                        <button
                          onClick={() => header.column.setFilterValue("")}
                          style={{
                            position: "absolute",
                            right: "4px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            border: "none",
                            background: "transparent",
                            cursor: "pointer",
                            fontSize: "12px",
                            color: "#888",
                          }}
                        >
                          ❌
                        </button>
                      ) : null}
                    </div>
                  ) : null}

                  {/* Resize handle */}
                  {header.column.getCanResize() && (
                    <div
                      onMouseDown={header.getResizeHandler()}
                      onTouchStart={header.getResizeHandler()}
                      style={{
                        position: "absolute",
                        right: 0,
                        top: 0,
                        height: "100%",
                        width: "1px",
                        cursor: "col-resize",
                        userSelect: "none",
                          touchAction: "none",
                          color: "black",
                        background: "black",
                      }}
                    />
                  )}
                </div>
              ))
            )}
          </div>

          {/* Rows */}
          <div
            style={{
              height: rowVirtualizer.getTotalSize(),
              width: "100%",
              position: "relative",
              textAlign: "center"
            }}
          >
            {rowVirtualizer.getVirtualItems().map((virtualRow) => {
              const row = table.getRowModel().rows[virtualRow.index];
              const isEven = virtualRow.index % 2 === 0;
              return (
                <div
                  key={row.id}
                  style={{
                    position: "absolute",
                    width: `${totalTableWidth}px`,
                    top: 0,
                    left: 0,
                    transform: `translateY(${virtualRow.start}px)`,
                    display: "grid",
                    gridTemplateColumns,
                    borderBottom: "1px solid #eee",
                    padding: "7px 0",
                    background: isEven ? "#fafafa" : "#f1f1f1",
                    gap: "4px",
                    transition: "background 0.2s ease-in-out",
                    cursor: "pointer",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "#dbeafe"; // light blue highlight
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = isEven ? "#fafafa" : "#f1f1f1";
                  }}
                >
                  {row.getVisibleCells().map((cell) => {
                    const colKey =
                      cell.column.columnDef.accessorKey ?? cell.column.id;

                    if (colKey === "Call_Message" || colKey === "Sms_Message") {
                      return (
                        <SimpleTooltip
                          key={cell.id}
                          content={String(cell.getValue() ?? "")}
                        >
                          <div
                            style={{
                              minWidth: cell.column.getSize(),
                              padding: "0 8px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </div>
                        </SimpleTooltip>
                      );
                    }

                    return (
                      <div
                        key={cell.id}
                        style={{
                          minWidth: cell.column.getSize(),
                          padding: "0 8px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>

        <div
          style={{
            marginTop: "8px",
            fontSize: "14px",
            fontWeight: "bold",
            textAlign: "right",
          }}
        >
          Records: {data.length}
        </div>
      </div>
    );
  }
);

export default VirtualizedTable;
